<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { activeLinkIndex } from '../navActive.js';
import { initials, styleVars } from '../Trailhead/shared.js';
import { menuLinks } from '../menuLinks.js';

// The top menu as the Hub draws it: the hub page's header row — logo
// tile, name, sub-line — on the org's colour band, with the site's
// links and a light CTA button where the hub has its Follow button.
// Same config, links and active-link rule as ThMenu, so a site switched
// either way keeps every link it had.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});
const editing = inject('tiqEditorCanvas', false);

const links = computed(() => menuLinks(props.config.links, props.site_data, editing));
const wordmark = computed(() => props.config.logo_text || props.org?.name || '');
const brandInitials = computed(() => initials(wordmark.value) || 'TQ');
const hasCta = computed(() => String(props.config.cta?.label ?? '').trim() !== '');
const logoFailed = ref(false);
const showLogo = computed(() => Boolean(props.org?.logo_url) && !logoFailed.value);

// Classic's menu names two style keys the shared styleVars does not
// (wordmark, link); honoured here as ThMenu does, so a color set under
// either template still lands after a switch.
const vars = computed(() => {
    const s = props.config.style || {};
    const v = { ...styleVars(props.config) };
    if (s.wordmark) v['--text-wordmark'] = s.wordmark;
    if (s.link) v['--text-link'] = s.link;
    return v;
});

// A server-rendered <img> starts loading before the page hydrates, so a
// logo that fails fast (a 404, a blocked host) has already fired its
// `error` before Vue attaches @error — and the visitor is left with a
// broken-image icon where the browser-built page shows the initials.
// So hydration asks the element what already happened.
const logoImg = ref(null);
const activeIndex = ref(-1);
onMounted(() => {
    activeIndex.value = activeLinkIndex(links.value, window.location);
    const img = logoImg.value;
    if (img && img.complete && img.naturalWidth === 0) logoFailed.value = true;
});

const drawerOpen = ref(false);
function closeDrawer() { drawerOpen.value = false; }
</script>

<template>
    <header :id="config.anchor_id || null" class="th-block hub-band hub-menu" :style="vars">
        <div class="th-wrap hub-menu__inner">
            <a href="/" class="hub-menu__brand">
                <span class="hub-menu__logo" :class="{ 'hub-menu__logo--img': showLogo }">
                    <img v-if="showLogo" ref="logoImg" :src="org.logo_url" :alt="org.name || ''" @error="logoFailed = true">
                    <span v-else aria-hidden="true">{{ brandInitials }}</span>
                </span>
                <span class="hub-menu__text">
                    <span class="hub-menu__name">{{ wordmark }}</span>
                </span>
            </a>

            <nav v-if="links.length" class="hub-menu__nav" aria-label="Site">
                <a v-for="(link, i) in links" :key="i"
                   :href="link.href || '#'"
                   class="hub-menu__link"
                   :class="{ 'is-active': i === activeIndex }"
                   :aria-current="i === activeIndex ? 'page' : null">{{ link.label }}</a>
            </nav>

            <a v-if="hasCta" :href="config.cta.href || '#'" class="th-btn th-btn--light th-btn--sm hub-menu__cta">{{ config.cta.label }}</a>

            <button v-if="links.length" type="button" class="hub-menu__burger"
                    :aria-expanded="drawerOpen ? 'true' : 'false'" aria-label="Menu"
                    @click="drawerOpen = !drawerOpen">
                <span></span><span></span><span></span>
            </button>
        </div>

        <div v-if="drawerOpen" class="th-wrap hub-menu__drawer">
            <a v-for="(link, i) in links" :key="i" :href="link.href || '#'"
               class="hub-menu__drawer-link" :class="{ 'is-active': i === activeIndex }"
               :aria-current="i === activeIndex ? 'page' : null" @click="closeDrawer">{{ link.label }}</a>
            <a v-if="hasCta" :href="config.cta.href || '#'" class="th-btn th-btn--light th-btn--block" @click="closeDrawer">{{ config.cta.label }}</a>
        </div>
    </header>
</template>

<style scoped>
.hub-menu { position: sticky; top: 0; z-index: 30; }
.hub-menu__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 14px;
    padding-top: 18px;
    padding-bottom: 18px;
}
.hub-menu__brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-right: auto;
    min-width: 0;
    color: inherit;
}
.hub-menu__logo {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    overflow: hidden;
    flex: none;
    background: var(--hub-detail, #dd9c34);
    color: var(--hub-head, #0e2419);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.02em;
}
.hub-menu__logo--img { background: #fff; padding: 4px; }
.hub-menu__logo img { width: 100%; height: 100%; object-fit: contain; }
.hub-menu__text { min-width: 0; display: flex; flex-direction: column; }
.hub-menu__name {
    color: var(--text-wordmark, inherit);
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.hub-menu__nav { display: flex; gap: 2px; overflow-x: auto; max-width: 100%; scrollbar-width: none; }
.hub-menu__nav::-webkit-scrollbar { display: none; }
.hub-menu__link {
    font-size: 13px;
    font-weight: 700;
    color: var(--text-link, inherit);
    padding: 8px 11px;
    border-radius: 9px;
    white-space: nowrap;
}
.hub-menu__link:hover { background: var(--hub-head-veil, rgba(255, 255, 255, .08)); }
.hub-menu__link.is-active { background: var(--hub-head-veil, rgba(255, 255, 255, .08)); box-shadow: inset 0 0 0 1px var(--hub-head-line, rgba(255, 255, 255, .18)); }
.hub-menu__burger {
    display: none;
    background: transparent;
    color: inherit;
    border: 1px solid var(--hub-head-line, rgba(255, 255, 255, .18));
    border-radius: 10px;
    padding: 9px 8px;
}
.hub-menu__burger span { display: block; width: 18px; height: 2px; background: currentColor; border-radius: 2px; }
.hub-menu__burger span + span { margin-top: 4px; }
.hub-menu__drawer { display: none; flex-direction: column; gap: 4px; padding-bottom: 16px; }
.hub-menu__drawer-link {
    padding: 11px 2px;
    font-weight: 700;
    font-size: 15px;
    color: inherit;
    border-bottom: 1px solid var(--hub-head-line, rgba(255, 255, 255, .18));
}
@media (max-width: 720px) {
    .hub-menu__nav, .hub-menu__cta { display: none; }
    .hub-menu__burger { display: block; }
    .hub-menu__drawer { display: flex; }
}
</style>
